import { domainRegionMap } from '@gap/utils/region-utils'
import { isProdLikeStage } from '@gap/utils/stage'
import { extractUrlRegion } from "reusable/utils/url-utils"
import { VimeoUrl } from '../app-services/vimeo/VimeoService'
import { UserDto } from '../app-services/user/models/UserDto'

const getRegionVariables = () => {
   const region = typeof window !== 'undefined' ? extractUrlRegion(window.location).region : 'au'
   const env = import.meta.env
   const regionVariables = !!region ? Object.entries(env).filter(([key]) => key.endsWith(`_${region.toUpperCase()}`)).reduce((acc, [key, value]) => {
      const newKey = key?.split('_') || []
      newKey.pop()
      acc[newKey.join('_')] = value
      return acc
   }, {} as any) : {}

   const userPoolDomainName = regionVariables.VITE_AUTH_USERPOOLDOMAINNAME || env.VITE_USER_POOL_DOMAIN
   const userPoolDomain = `${userPoolDomainName}.auth.${domainRegionMap[region]}.amazoncognito.com`

   return {
      userPoolId: regionVariables.VITE_AUTH_USERPOOLID || env.VITE_USER_POOL_ID as string,
      userPoolDomain,
      identityPoolId: regionVariables.VITE_AUTH_USERIDENTITYPOOLCLIENTID || env.VITE_IDENTITY_POOL_ID as string,
      appUserPoolClientId: regionVariables.VITE_AUTH_USERPOOLCLIENTID || env.VITE_USER_POOL_CLIENT_ID as string,
      // v3 (infra-v3 SiteStack) publishes the squished VITE_AUTH_BACKENDUSERPOOLCLIENTID; v2's build
      // pipeline (deploy-sst-region / dryrun.sh) still emits the legacy underscored name. Accept both so
      // the guest WS client id resolves on v2-served hosts too — without it guests get undefined, the WS
      // authorizer rejects the connection, and uploads time out as "file is invalid".
      backendUserPoolClientId: regionVariables.VITE_AUTH_BACKENDUSERPOOLCLIENTID || regionVariables.VITE_AUTH_BACKEND_USER_POOL_CLIENT_ID || env.VITE_BACKEND_USER_POOL_CLIENT_ID as string,
      apiUrl: regionVariables.VITE_API_URL || env.VITE_API_URL as string,
      webSocketUrl: regionVariables.VITE_WS_URL || env.VITE_GAP_WEBSOCKET_URL as string,
      region: domainRegionMap[region] as string,
      regionBaseUrl: `${import.meta.env.VITE_APP_HOST}/${region}`,
      transcribeIdentityPoolId: (regionVariables.VITE_TRANSCRIBE_IDENTITY_POOL_ID || env.VITE_TRANSCRIBE_IDENTITY_POOL_ID || '') as string,
   }
}

const variables = {
   cognitoOauthScopes: import.meta.env.VITE_COGNITO_OAUTH_SCOPES?.split(',') || [],
   stage: import.meta.env.VITE_STAGE as string,
   gapOrgId: '00gap', //import.meta.env.VITE_GAP_ORG_ID as string,
   amplifyApiName: 'back-end',
   gapDomain: 'thegaphq.com',
   devTeam: ['kevin', 'guus', 'jason'],
   productTeam: ['jeremy', 'sarah', 'chris'],
   gapName: 'The Gap',
   gapAI: 'Gap AI',
   gapSupportEmail: 'support@app.thegaphq.com',
   defaultTimeZone: import.meta.env.VITE_DEFAULT_TIME_ZONE as string,
   workspaceBackgroundColor: '#f7f6f7',
   enableRouteLogs: import.meta.env.VITE_ENABLE_ROUTE_LOGS as string,
   gapCryptoKey: import.meta.env.GAP_CRYPTO_KEY as string,
   advisorWelcomeVideoUrl: import.meta.env.VITE_ADVISOR_WELCOME_VIDEO_URL as VimeoUrl | undefined,
   meetingTourVideoUrl: import.meta.env.VITE_MEETING_TOUR_VIDEO_URL as VimeoUrl | undefined,
   contentPadding: '24px 64px 24px 64px',
   contentPaddingY: '24',
   contentPaddingX: '64',
   appBarHeight: '64',
   trialBarHeight: '40',
   gapIdTokenLocalStorageKey: 'gap:idToken',
   gapAccessTokenLocalStorageKey: 'gap:accessToken',
   gapRefreshTokenLocalStorageKey: 'gap:refreshToken',
   tokenRefreshInterval: 1000 * 60 * 10, // 10 minutes,
   allowPreworkDocEditing: import.meta.env.VITE_ALLOW_PREWORK_DOC_EDITING,
   pricingUrl: 'https://thegaphq.com/pricing',
   quickFillPassword: import.meta.env.VITE_QUICK_FILL_PASSWORD,
   useQuickFill: import.meta.env.VITE_USE_QUICK_FILL === 'true',
   // Test-autofill is active only when the build flag is on AND we're not on a prod-like stage.
   // Centralised here so the four autofill call sites don't each re-derive the stage check.
   get quickFillActive(): boolean { return this.useQuickFill && !isProdLikeStage(this.stage) },
   isMultiRegion: !!(import.meta.env.VITE_MULTI_REGION && import.meta.env.VITE_MULTI_REGION !== '0'),
   ...getRegionVariables()
}

export const UserOnboardingDefaults: UserDto.Onboarding = {
   hasViewedWelcome: false,
   hasViewedTacticAnnouncement: false,
   showCheckList: true,
   hasSeenMeetingGuidedTour: false,
   hasConsentedToTranscription: false,
   completedSteps: [],
   gettingStartedSteps: ['setupAccount'],
   gettingStartedSeenSteps: [],
   gettingStartedCollapsed: false,
   gettingStartedCompletedAt: undefined,
}

export default variables
